import moment from "moment";
import * as XLSX from "xlsx";
import { PfApiError, request } from "pages/PayRoll/Configuration/StatutoryConfiguration/PF/pfConfigApi";
import { extraOf, formatDay } from "./pfMembersData";

// PF Members (Payroll > Statutory > PF, screens P4–P6b). The screens keep
// the row shape the sample data had ("Member", "Opted out", labels ready to
// show); these mappers convert from the API shape (MEMBER, "30000.00", ...).

const BASE = "payroll/statutory/pf/";

const STATUS = { MEMBER: "Member", OPTED_OUT: "Opted out", NOT_IN_PF: "Not in PF" };
const RESULT = { VALID: "Valid", CHANGE: "Change", NO_CHANGE: "No change", ERROR: "Error" };

const text = (value) => (value == null ? "" : String(value));
const number = (value) => Number(value) || 0;
const monthKey = (value) => (value ? String(value).slice(0, 7) : "");
const payload = (data) => {
  if (!data || typeof data !== "object" || !("data" in data)) throw new PfApiError("The server returned an invalid response.");
  return data.data;
};
const call = (options) => request({ base: BASE, ...options });

// ---- Mappers ----

// PF settings the drawers need. Rates live in the contribution formulas,
// so the version's components come along for the Extra PF preview.
// null = PF is not set up for the company.
export const fromApiSettings = (api) =>
  api
    ? {
        versionId: api.version_id ?? null,
        ceiling: number(api.wage_ceiling),
        epsCap: number(api.eps_cap),
        stopAge: number(api.eps_stop_age),
        basis: api.pf_basis_default || "",
        components: (api.components || []).map((component) => ({
          code: component.code,
          kind: component.kind,
          formula: component.formula,
          status: component.status === "ACTIVE" ? "Active" : "Inactive",
        })),
        vpfEnabled: Boolean(api.vpf_enabled),
        vpfMaxPct: number(api.vpf_max_pct),
        actualAllowed: Boolean(api.vpf_on_actual_allowed),
        optOutAllowed: Boolean(api.opt_out_allowed),
      }
    : null;

export const pensionLabel = (pension, reason, settings) => {
  switch (pension) {
    case "YES":
      return { label: "Yes", tone: "green" };
    case "NO":
      return { label: reason === "import" ? "No · from Import UAN" : `No · ${reason || "joined after Sep 2014 above ceiling"}`, tone: "slate" };
    case "STOPPED":
      return { label: `No · age ${settings?.stopAge || 58}`, tone: "slate" };
    case "UNKNOWN":
      return { label: "Unknown", tone: "red" };
    case "DOB_NEEDED":
      return { label: "DOB needed", tone: "red" };
    default:
      return { label: "—", tone: null };
  }
};

export const pfCodeLabel = (code) => (code ? `${code.type === "MAIN" ? "Main" : "Ext"} · …${text(code.code).slice(-6)}` : "Not linked");

export const fromApiMember = (api, settings) => {
  const status = STATUS[text(api.status).toUpperCase().replace(/[\s-]+/g, "_")] || text(api.status);
  const uan = text(api.uan);
  const dob = text(api.dob);
  const extra = api.extra || null;
  const row = {
    id: api.id,
    emp_id: text(api.employee_code),
    name: text(api.name),
    department: text(api.department),
    location: text(api.location),
    status,
    uan,
    uan_label: uan || (status === "Member" ? "To be generated" : "—"),
    dob,
    age: api.age ?? null,
    dob_label: dob ? `${formatDay(dob)} (${api.age})` : "Missing",
    pf_code: api.pf_code || null,
    pf_code_label: pfCodeLabel(api.pf_code),
    pf_wage: number(api.pf_wage),
    pension: api.pension || null,
    full_salary: Boolean(extra?.full_salary),
    extra_amount: number(extra?.extra_amount),
    extra_from: monthKey(extra?.from_month),
    extra_set_by: text(extra?.set_by),
    needs_fix: Boolean(api.needs_fix),
  };
  const pension = pensionLabel(api.pension, api.pension_reason, settings);
  const extraLabel = extraOf(row);
  return { ...row, pension_label: pension.label, pension_tone: pension.tone, extra_label: extraLabel.label, extra_tone: extraLabel.tone };
};

export const fromApiMemberDetails = (api, settings) => ({
  ...fromApiMember(api, settings),
  date_of_joining: text(api.date_of_joining),
  joined_pf: text(api.pf_joined_on),
  previous_pf_member: Boolean(api.previous_pf_member),
  eps_member: text(api.eps_member),
  eps_source: text(api.eps_member_source),
  pension_stops_month: text(api.pension_stops_month),
  opt_out_from_type: text(api.opt_out_from_type),
  opt_out_from: text(api.opt_out_from),
  opt_out_from_label:
    api.opt_out_from_type === "DATE_OF_JOINING" ? "Date of joining" : api.opt_out_from ? moment(api.opt_out_from).format("MMM YYYY") : "",
  opt_out_reason: text(api.opt_out_reason),
  opt_out_form11_name: text(api.opt_out_form11_name),
  opt_out_form11_url: api.opt_out_form11_url || "",
  extra_history: (api.extra_history || []).map((item) => ({
    id: item.id,
    from: monthKey(item.from_month),
    full_salary: Boolean(item.full_salary),
    extra_amount: number(item.extra_amount),
    letter_name: text(item.letter_name),
    letter_url: item.letter_url || "",
    set_by: text(item.set_by),
    set_at: text(item.set_at),
  })),
  row_version: api.row_version ?? null,
});

const fromApiResult = (item) => ({
  key: item.row,
  row: item.row,
  employee_id: text(item.employee_id) || "—",
  name: text(item.name) || "—",
  uan: text(item.uan) || "—",
  result: RESULT[item.result] || text(item.result),
  message: text(item.message),
});

// ---- Calls ----

export const fetchPfMembers = async ({ domain, companyId, search, card = "all", page = 1, pageSize = 50, ordering = "name", signal }) => {
  const query = { company_id: companyId, card, page, page_size: pageSize, ordering };
  if (search && search.trim()) query.search = search.trim();
  const data = payload(await call({ domain, path: "members/", query, signal }));
  const settings = fromApiSettings(data.settings);
  return {
    rows: (data.results || []).map((row) => fromApiMember(row, settings)),
    count: number(data.count),
    page: number(data.page) || page,
    pageSize: number(data.page_size) || pageSize,
    cards: data.cards || {},
    settings,
  };
};

export const fetchPfMember = async ({ domain, companyId, employeeId, signal }) => {
  const data = payload(await call({ domain, path: `members/${employeeId}/`, query: { company_id: companyId }, signal }));
  const settings = fromApiSettings(data.settings);
  // The PF settings of the details response, used when the list has none.
  return { ...fromApiMemberDetails(data, settings), settings };
};

// JSON, or multipart when a file is attached (letter / Form 11).
const body = (fields, files) => {
  const picked = Object.entries(files).filter(([, file]) => file);
  if (!picked.length) return fields;
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) form.append(key, typeof value === "boolean" ? String(value) : value);
  });
  picked.forEach(([key, file]) => form.append(key, file));
  return form;
};

const saved = async (promise) => {
  const data = await promise;
  const member = payload(data);
  return { member: fromApiMemberDetails(member, fromApiSettings(member.settings)), message: text(data.message) };
};

// form: { fullSalary, extra, from: "YYYY-MM", letter: File | null }
export const savePfExtra = ({ domain, companyId, employeeId, form, rowVersion }) =>
  saved(
    call({
      domain,
      path: `members/${employeeId}/extra/`,
      method: "POST",
      body: body(
        {
          company_id: companyId,
          from_month: form.from,
          full_salary: Boolean(form.fullSalary),
          extra_amount: Number(form.extra) || 0,
          row_version: rowVersion,
        },
        { letter: form.letter },
      ),
    }),
  );

// form: { from: "DATE_OF_JOINING" | "YYYY-MM", reason, form11: File | null }
export const savePfOptOut = ({ domain, companyId, employeeId, form, rowVersion }) =>
  saved(
    call({
      domain,
      path: `members/${employeeId}/opt-out/`,
      method: "POST",
      body: body(
        {
          company_id: companyId,
          ...(form.from === "DATE_OF_JOINING" ? { from_type: "DATE_OF_JOINING" } : { from_type: "MONTH", from_month: form.from }),
          reason: form.reason,
          row_version: rowVersion,
        },
        { form11: form.form11 },
      ),
    }),
  );

export const endPfOptOut = ({ domain, companyId, employeeId, rowVersion }) =>
  saved(call({ domain, path: `members/${employeeId}/end-opt-out/`, method: "POST", body: { company_id: companyId, row_version: rowVersion } }));

const saveBlob = (blob, fileName) => {
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
};

// Keeps the header row + the first `count` data rows of the first sheet
// (the server fills the template with every PF member). Any read error
// keeps the file as the server sent it.
const trimTemplate = async (blob, count) => {
  try {
    const book = XLSX.read(await blob.arrayBuffer(), { type: "array", cellStyles: true });
    const sheet = book.Sheets[book.SheetNames[0]];
    if (!sheet || !sheet["!ref"]) return blob;
    // Cut the sheet in place so cell formats (dates, text UANs) stay.
    const range = XLSX.utils.decode_range(sheet["!ref"]);
    const lastRow = Math.min(range.e.r, range.s.r + count);
    Object.keys(sheet).forEach((address) => {
      if (address[0] !== "!" && XLSX.utils.decode_cell(address).r > lastRow) delete sheet[address];
    });
    range.e.r = lastRow;
    sheet["!ref"] = XLSX.utils.encode_range(range);
    return new Blob([XLSX.write(book, { type: "array", bookType: "xlsx" })], { type: blob.type || "application/octet-stream" });
  } catch (error) {
    return blob;
  }
};

// sampleRows: keep only that many example rows in the downloaded file.
export const downloadUanTemplate = async ({ domain, companyId, format = "xlsx", sampleRows }) => {
  const { blob, fileName } = await call({
    domain,
    path: "uan-import/template/",
    query: { company_id: companyId, ...(format === "csv" ? { format: "csv" } : {}) },
    responseType: "blob",
  });
  const name = fileName || `PF UAN Import Template.${format === "csv" ? "csv" : "xlsx"}`;
  saveBlob(sampleRows && format !== "csv" ? await trimTemplate(blob, sampleRows) : blob, name);
  return name;
};

// Letters / Form 11 are streamed behind auth, so they open from a blob.
export const openPfFile = async ({ domain, url }) => {
  const { blob } = await call({ domain, url, responseType: "blob" });
  const href = URL.createObjectURL(blob);
  window.open(href, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(href), 60000);
};

export const fetchUanMapping = async ({ domain, companyId, signal }) => {
  const data = payload(await call({ domain, path: "uan-import/mapping/", query: { company_id: companyId }, signal }));
  return data?.mapping || null;
};

export const newRequestKey = () =>
  typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

// rows: [{ employee_id, employee_name, uan, pf_joined_on, previous_pf_member, eps_member }] (raw text)
export const previewUanImport = async ({ domain, companyId, requestKey, fileName, rows, mapping }) => {
  const data = payload(
    await call({
      domain,
      path: "uan-import/preview/",
      method: "POST",
      body: { company_id: companyId, request_key: requestKey, file_name: fileName, rows, mapping },
    }),
  );
  return { batchId: data.batch_id, totals: data.totals || {}, results: (data.results || []).map(fromApiResult) };
};

export const commitUanImport = async ({ domain, companyId, batchId, requestKey }) => {
  const data = await call({
    domain,
    path: `uan-import/${batchId}/commit/`,
    method: "POST",
    body: { company_id: companyId, request_key: requestKey },
  });
  return { ...payload(data), message: text(data.message) };
};
